import {
  CAPABILITY_LABELS,
  type CapabilityId,
  type EngineTarget,
  type LimitationWire,
  modelLabel,
  parseModelRef,
  type RuntimeProvider,
} from "@mfa/v5-contracts";
import type { IntlShape } from "react-intl";

/**
 * Kid-facing labels for runtime profiles (Template Strategy V2 §8.1): the
 * friendly model name plus "runs on <provider>" — NEVER a raw
 * `<provider>/<model>` key or a bare id. One home so the picker, the run
 * console, History and the error copy agree.
 */

/** Provider names are proper nouns (allow-listed in tests/i18n.test.ts) behind one message id each. */
export const PROVIDER_MESSAGE: Readonly<Record<RuntimeProvider, string>> = {
  anthropic: "provider.anthropic",
  openrouter: "provider.openrouter",
  foundry: "provider.foundry",
};

export function providerLabel(intl: IntlShape, provider: RuntimeProvider): string {
  return intl.formatMessage({ id: PROVIDER_MESSAGE[provider] });
}

/**
 * `<provider>/<model>` → its parts, or null when the key is not a runtime profile key (a bare id
 * is not). `model` is the canonical catalog id when the key names a provider's native name
 * (`openrouter/anthropic/claude-haiku-4.5` → `claude-haiku-4-5`).
 */
export function splitProfileKey(key: string): { provider: RuntimeProvider; model: string } | null {
  const ref = parseModelRef(key);
  return ref?.provider ? { provider: ref.provider, model: ref.canonical } : null;
}

/** "runs on Anthropic" */
export function runsOnLabel(intl: IntlShape, provider: RuntimeProvider): string {
  return intl.formatMessage({ id: "profile.runsOn" }, { provider: providerLabel(intl, provider) });
}

/**
 * The path a run took where its provider has two ("sandbox service", "server loop"); null for an
 * Anthropic run, whose provider chip already says where. The label alone, never the rule's
 * reasons (decision D4: those stay in /dev and the sweep output).
 */
export function runnerLabel(
  intl: IntlShape,
  target: EngineTarget | null | undefined,
): string | null {
  if (target !== "loop" && target !== "service") return null;
  return intl.formatMessage({ id: `console.runner.${target}` });
}

/** "Sonnet 5 · runs on Anthropic" for a bare key; a non-profile string displays as-is. */
export function formatProfileKey(intl: IntlShape, key: string): string {
  const parts = splitProfileKey(key);
  if (!parts) return key;
  return `${modelLabel(parts.model)} · ${runsOnLabel(intl, parts.provider)}`;
}

/** What a model CHOICE reads as: the friendly name, plus "runs on <provider>" when the id names one. */
export function modelChoiceLabel(intl: IntlShape, id: string): string {
  const ref = parseModelRef(id);
  return ref?.provider
    ? `${modelLabel(ref.canonical)} · ${runsOnLabel(intl, ref.provider)}`
    : modelLabel(id);
}

/** The capability's friendly name in the viewer's locale ("web search"). */
export function capabilityLabel(limitation: LimitationWire, locale: string): string {
  const labels = CAPABILITY_LABELS[limitation.capability as CapabilityId];
  if (!labels) return limitation.capability;
  return locale.startsWith("es") ? labels.es : labels.en;
}
